import React, { FunctionComponent, useEffect, useState } from "react";
import { Route, Routes as Switch } from "react-router";
import { RoutesBuilder } from "./models/RoutesBuilder";
import { Login } from "./pages/Login";
import { Albums } from "./pages/Albums";
import { Attributes } from "./pages/Attributes";
import { LanguageManager } from "./models/LanguageManager";
import { strings } from "./localization/LocalizedStrings";
import { Stats } from "./pages/Stats";
import { MaintenancePage } from "./pages/Maintenance";
import { MarketplacePage } from "./pages/Marketplace";
import { YouTubePlayer } from "./components/YouTubePlayer";
import { NotFoundPage } from "./pages/NotFound";
import { DocumentationRoutes } from "./components/documentation/DocumentationRoutes";
import { PaymentPage } from "./pages/Payment";
import { useSessionManager, useUser } from "./hooks/session";
import { AdminConsole } from "./pages/AdminConsole";
import { UserProfile } from "./pages/UserProfile";
import { toast } from "react-toastify";
import { Firebase } from "./services/Firebase";
import { useSavePushNotificationToken } from "./hooks/pushNotificationTokens";
import { onMessage } from "firebase/messaging";
import { Layout } from "./pages/Layout";
import { PaymentPatreonPage } from "./pages/PaymentPatreon";
import { StatusCodesHelper } from "./models/StatusCodesHelper";
import { useIsMobile } from "./hooks/isMobile";
import { IconType } from "./icons";

export const Routes: FunctionComponent = () => {
  const isMobile = useIsMobile();
  const { getToken } = useSessionManager();
  const { savePushNotificationToken } = useSavePushNotificationToken();
  const [currentUser] = useUser.useState();
  const [videoIds, setVideoIds] = useState<string[]>();
  const [playerTitle, setPlayerTitle] = useState("");
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);

  useEffect(() => {
    const icons = Object.values(IconType);

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=block&icon_names=${icons.sort().join(",")}`;
    link.onload = () => document.documentElement.classList.add("icons-ready");
    document.head.appendChild(link);
  }, []);

  if (Firebase.shared.messaging) {
    onMessage(Firebase.shared.messaging, payload => {
      toast(payload.notification?.body);
    });
  }

  if (process.env.MAINTENANCE == "true") {
    return <MaintenancePage />;
  }

  const language = LanguageManager.getLanguage();
  if (language) {
    strings.setLanguage(language);
  }

  if (
    !window.location.pathname.includes(RoutesBuilder.documentation.home()) &&
    !window.location.pathname.includes(RoutesBuilder.signIn()) &&
    !window.location.pathname.includes(RoutesBuilder.maintenance()) &&
    !window.location.pathname.includes(RoutesBuilder.notFound())
  ) {
    const token = getToken();
    if (!token) {
      return <Login isMinimalist={false} />;
    } else {
      Firebase.shared.getFCMToken(fcmToken => {
        if (fcmToken) {
          if (currentUser?.role != "guest") {
            const cachedToken = localStorage.getItem("fcm_token") ?? "";
            if (cachedToken != fcmToken) {
              window.navigator.mediaDevices.enumerateDevices().then(async devices => {
                const { status } = await savePushNotificationToken({
                  token: fcmToken,
                  deviceId: devices[0].deviceId
                });
                if (StatusCodesHelper.isSuccessful(status)) {
                  localStorage.setItem("fcm_token", fcmToken);
                }
              });
            }
          }
        } else if (!isMobile) {
          toast(strings.push_notifications_permission_notice, {
            autoClose: false,
            onClick: Firebase.shared.requestPermission,
            onClose: Firebase.shared.requestPermission,
            toastId: "firebase-messaging",
            closeOnClick: true,
            delay: 50000
          });
        }
      });
    }
  }

  const onPlayFromYouTube = (title: string, videoIds: string[]) => {
    setPlayerTitle(title);
    setVideoIds(videoIds);
    setIsPlayerOpen(true);
  };

  return (
    <>
      <Switch>
        <Route
          path={RoutesBuilder.home()}
          element={<Albums isPublic={false} onPlayFromYouTube={onPlayFromYouTube} />}
        />
        <Route
          path={RoutesBuilder.guest()}
          element={<Albums isPublic={false} onPlayFromYouTube={onPlayFromYouTube} />}
        />
        <Route path={RoutesBuilder.signIn()} element={<Login isMinimalist={false} />} />
        <Route
          path={RoutesBuilder.albums.list()}
          element={<Albums isPublic={false} onPlayFromYouTube={onPlayFromYouTube} />}
        />
        <Route path={RoutesBuilder.attributes.manage()} element={<Attributes />} />
        <Route path={RoutesBuilder.userProfile()} element={<UserProfile />} />
        <Route path={RoutesBuilder.stats()} element={<Stats />} />
        <Route path={RoutesBuilder.marketplace()} element={<MarketplacePage />} />
        <Route
          path={RoutesBuilder.layout()}
          element={<Layout onPlayFromYouTube={onPlayFromYouTube} />}
        />
        <Route path={RoutesBuilder.admin()} element={<AdminConsole />} />
        <Route
          key={"public_albums"}
          path={RoutesBuilder.share()}
          element={<Albums isPublic={true} onPlayFromYouTube={onPlayFromYouTube} />}
        />
        <Route
          key={"private_albums"}
          path={RoutesBuilder.private()}
          element={<Albums isPublic={false} onPlayFromYouTube={onPlayFromYouTube} />}
        />
        <Route path={RoutesBuilder.payment()} element={<PaymentPage />} />
        <Route path={RoutesBuilder.paymentPatreon()} element={<PaymentPatreonPage />} />
        <Route path={`${RoutesBuilder.documentation.home()}/*`} element={<DocumentationRoutes />} />
        <Route
          key={"maintenance"}
          path={RoutesBuilder.maintenance()}
          element={<MaintenancePage />}
        />
        <Route key={"not_found"} path={"*"} element={<NotFoundPage />} />
      </Switch>
      <YouTubePlayer
        title={playerTitle}
        videoIds={videoIds}
        open={isPlayerOpen}
        setOpen={setIsPlayerOpen}
      />
    </>
  );
};
